'use client';

import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { modalActions } from 'src/store/client/slices/modalSlice';
import { checkLogin } from 'src/utils/auth';

import Skeleton from '@/app/(football)/[team]/_components/common/Skeleton';
import StickerCard from '@/app/(football)/[team]/_components/common/StickerCard';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import SetDefaultCardModal, {
  SET_DEFAULT_CARD_MODAL_KEY,
} from '@/app/(football)/[team]/main/_components/Modal/SetDefaultCardModal';
import { useAppDispatch } from '@/store/client/config';
import { useGetRepresentCardWithInitQuery } from '@/store/server/common/football/football.query';

const REP_CARD_CONTAINER_WIDTH = 155;
const REP_CARD_CONTAINER_HEIGHT = 240;

export default function RepresentativeCard() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { originalsName, teamSlug, packName, externalLinks, stickerCategories, history } = useTeamConfig();
  const { data: representCardData, isLoading, isError } = useGetRepresentCardWithInitQuery({ originalsName });

  const handleOpenEditCardModal = () => {
    if (isError) {
      alert('다시 접속해 주세요.');
      return;
    }

    if (!checkLogin()) {
      router.push(`/account/login?retUrl=${pathname}`);
      return;
    }

    dispatch(
      modalActions.openComponentModal({
        name: SET_DEFAULT_CARD_MODAL_KEY,
        type: 'strong',
        motion: {
          type: 'drawer',
          placement: 'bottom',
        },
        component: (
          <SetDefaultCardModal
            originalsName={originalsName}
            packName={packName}
            teamSlug={teamSlug}
            externalLinks={externalLinks}
            stickerCategories={stickerCategories}
            history={history}
          />
        ),
      }),
    );
  };

  // Card state
  const isCardDataLoading = !isError && isLoading;
  const isCardDataAvailable = !isError && !isLoading && representCardData;
  const isCardDataEmpty = !isError && !isLoading && !representCardData;

  return (
    <div className="flex w-full flex-col items-center justify-center py-[48px] pb-[66px]">
      {/* 1. Loading */}
      {isCardDataLoading && <Skeleton width={REP_CARD_CONTAINER_WIDTH} height={REP_CARD_CONTAINER_HEIGHT} borderRadius={6} />}

      {/* 2. Error or Empty */}
      {(isError || isCardDataEmpty) && (
        <button
          onClick={handleOpenEditCardModal}
          className="cursor-pointer overflow-hidden rounded-[6px] bg-transparent"
          style={{ width: REP_CARD_CONTAINER_WIDTH, height: REP_CARD_CONTAINER_HEIGHT }}
        >
          <Image
            src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/main/add_representative.webp`}
            alt="Add Representative Card"
            width={REP_CARD_CONTAINER_WIDTH}
            height={REP_CARD_CONTAINER_HEIGHT}
          />
        </button>
      )}

      {/* 3. Card Available */}
      {isCardDataAvailable && (
        <div className="relative" style={{ width: REP_CARD_CONTAINER_WIDTH, height: REP_CARD_CONTAINER_HEIGHT }}>
          <div className="overflow-hidden rounded-[6px]" style={{ width: REP_CARD_CONTAINER_WIDTH, height: REP_CARD_CONTAINER_HEIGHT }}>
            <StickerCard
              mode="view"
              image={representCardData.image}
              width={representCardData.width}
              height={representCardData.height}
              stickers={representCardData.stickers}
              containerSize={{ width: REP_CARD_CONTAINER_WIDTH, height: REP_CARD_CONTAINER_HEIGHT }}
            />
          </div>

          {/* Edit Button */}
          <button
            onClick={handleOpenEditCardModal}
            className="absolute right-[-10px] bottom-[-10px] cursor-pointer rounded-full"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11.5" fill="var(--color-button-representative-cardedit-default-bg)" stroke="var(--color-button-representative-cardedit-default-border)" />
              <path
                d="M7.88525 13.7422L10.2423 16.0992L7.69782 16.6864C7.4578 16.7418 7.24268 16.5267 7.29807 16.2866L7.88525 13.7422Z"
                fill="var(--color-button-representative-cardedit-default-icon)"
              />
              <path
                d="M15.1925 6.43492C15.3227 6.30475 15.5338 6.30475 15.6639 6.43492L17.5495 8.32054C17.6797 8.45071 17.6797 8.66177 17.5495 8.79194L10.7142 15.6273L8.35715 13.2703L15.1925 6.43492Z"
                fill="var(--color-button-representative-cardedit-default-icon)"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
